import type { SupportedLanguage } from '../../../utils/languageDetection';

export type SkillRunnerCopy = {
  welcomePaste: string;
  errorEmptyPaste: string;
  foundRequests: (count: number) => string;
  errorAnalyze: (msg: string) => string;
  askLocation: string;
  decideYourself: string;
  autoModeNotice: string;
  askChronological: string;
  askOuterClauseMatchYes: string;
  askOuterClauseMatchNo: string;
  appendAtLocationNotice: (locationLabel: string) => string;
  askFormat: string;
  chronologicalYes: string;
  chronologicalNo: string;
  formatTrackChanges: string;
  formatPlain: string;
  askAuthor: string;
  authorMyName: (name: string) => string;
  authorUnknown: string;
  authorSomeoneElse: string;
  askCustomAuthor: string;
  defaultAuthor: string;
  installing: string;
  doneSummary: (succeeded: number, failed: number) => string;
  noneInserted: string;
  errorExecute: (msg: string) => string;
  placeholderPaste: string;
  placeholderAuthor: string;
  /** Automatic LLM self-check, run right after auto-mode's own insertion
   * completes, BEFORE the manual reposition safety net below — no lawyer
   * instruction, the model inspects the resulting document on its own. */
  selfChecking: string;
  selfCheckFoundIssues: (labels: string[]) => string;
  selfCheckFixed: (count: number) => string;
  selfCheckAllCorrect: string;
  /** General post-execution safety net, asked after EVERY successful insertion
   * (not just when the agent itself flagged low-confidence items) — "did
   * anything land wrong, want me to move it?". */
  askReposition: string;
  repositionYes: string;
  repositionNo: string;
  askRepositionInstruction: string;
  placeholderReposition: string;
  repositioning: string;
  repositionDone: (moved: number) => string;
  repositionNone: string;
  errorReposition: (msg: string) => string;
  askRepositionAgain: string;
  /** Ask-heading-first flow: items the strategy loop held back (not inserted)
   * because it was not confident which existing heading they belong under. */
  askPendingHeading: (items: string[], headings: string[]) => string;
  placingPending: string;
  pendingPlacedDone: (placed: number, headingLabel: string) => string;
  errorPlacePending: (msg: string) => string;
  /** Upfront strategy proposal (auto mode): find every appendix/agreement
   * citation group in the pasted text BEFORE inserting anything. */
  analyzingStrategy: string;
  errorProposeStrategy: (msg: string) => string;
  /** Per-group wizard: one question per citation group, all data already in
   * hand client-side (see SkillWizardState) — no server round-trip between
   * questions, only a progress counter advancing locally. */
  wizardIntro: (totalGroups: number) => string;
  wizardProgress: (current: number, total: number) => string;
  wizardQuestion: (count: number, signal: string, rationale: string) => string;
  wizardQuestionNoSignal: (count: number, rationale: string) => string;
  wizardOptionOther: string;
  askNewHeadingText: string;
  placeholderNewHeading: string;
  wizardBackToOptions: string;
  errorEmptyNewHeading: string;
  newHeadingConfirmed: (headingText: string) => string;
  wizardAllAnswered: string;
  /** Posted once, as a single chat message, right after the last wizard
   * question is answered — the only place the wizard's decisions show up in
   * the transcript, since the questions themselves render in a dedicated
   * step-through card rather than as growing chat turns. */
  wizardSummaryHeader: string;
  wizardDecisionLine: (count: number, signal: string, headingLabel: string) => string;
  generalGroupLabel: string;
  wizardSubmit: string;
};

const COPY: Record<SupportedLanguage, SkillRunnerCopy> = {
  he: {
    welcomePaste: 'העתק/י והדבק/י את בקשות הלקוח מהמייל בתיבה למטה, ולחץ/י Enter לשליחה.',
    errorEmptyPaste: 'יש להדביק את בקשות הלקוח מהמייל.',
    foundRequests: (count) =>
      count === 1
        ? 'מצאתי בקשת לקוח אחת. היכן להתקין אותה?'
        : `מצאתי ${count} בקשות לקוח. היכן להתקין אותן?`,
    errorAnalyze: (msg) => `מצטער/ת, לא הצלחתי לנתח את התוכן שהודבק: ${msg}`,
    askLocation: '',
    decideYourself: 'תחליט בעצמך',
    autoModeNotice:
      'בסדר — אחליט/י לכל בקשה את המיקום המתאים. בקשות עם הפניה מפורשת לסעיף שבגוף המסמך יותקנו ליד הפסקה הרלוונטית; בקשות כלליות יועדפו תחת "כללי" / "הערות כלליות" אם קיים במסמך.',
    askChronological: 'האם להתקין את בקשות הלקוח לפי סדר כרונולוגי?',
    askOuterClauseMatchYes: 'כן, לפי ההפניות לסעיפים שבגוף המסמך',
    askOuterClauseMatchNo: 'לא, תתקין את הבקשות כהמשך ישיר של הסעיף האחרון',
    appendAtLocationNotice: (locationLabel) =>
      `בסדר — אתקין/ה את כל הבקשות בתחתית "${locationLabel}" כהמשך ישיר החל מהסעיף האחרון.`,
    askFormat: 'האם להתקין עם שינויי מעקב או כטקסט רגיל?',
    chronologicalYes: 'כן, לפי סדר כרונולוגי',
    chronologicalNo: 'כהמשך ישיר החל מהסעיף האחרון',
    formatTrackChanges: 'שינויי מעקב',
    formatPlain: 'טקסט רגיל',
    askAuthor: 'בשם מי יש לייחס את שינויי המעקב?',
    authorMyName: (name) => `בשמי: ${name}`,
    authorUnknown: 'Unknown',
    authorSomeoneElse: 'בשם מישהו אחר שאגדיר לך את שמו',
    askCustomAuthor:
      'מה שם הכותב שאותו אייחס ל"עקוב אחר שינויים" שאשתיל במסמך?',
    defaultAuthor: 'Unknown',
    installing: 'מתקין/ה את בקשות הלקוח במסמך…',
    doneSummary: (succeeded, failed) =>
      failed > 0
        ? `בוצע! הותקנו ${succeeded} בקשות לקוח (${failed} נכשלו).`
        : succeeded === 1
          ? 'בוצע! הותקנה בקשת לקוח אחת.'
          : `בוצע! הותקנו ${succeeded} בקשות לקוח.`,
    noneInserted: 'לא הותקנו בקשות לקוח. בדוק/י את המסמך ונסה/י שוב.',
    errorExecute: (msg) => `מצטער/ת, ההתקנה נכשלה: ${msg}`,
    placeholderPaste: 'הדבק/י בקשות לקוח מהמייל…',
    placeholderAuthor: 'למשל: שם הלקוח (השאר/י ריק ל-Unknown)',
    selfChecking: 'בודק/ת שהשתילה בוצעה כמו שצריך…',
    selfCheckFoundIssues: (labels) =>
      `ביצעתי בדיקה וזיהיתי שהסעיפים הבאים לא מוקמו נכון: ${labels.join(', ')}. מתקן/ת...`,
    selfCheckFixed: (count) =>
      count === 1 ? 'תוקן! סעיף אחד הועבר למיקום הנכון.' : `תוקן! ${count} סעיפים הועברו למיקום הנכון.`,
    selfCheckAllCorrect: 'בדקתי את מיקום הסעיפים — הכל תקין.',
    askReposition: 'האם ישנם סעיפים שלא הושתלו כראוי ותרצה שאמקם אותם מחדש? אם כן תגיד לי מה ואיפה למקם.',
    repositionYes: 'כן, אני רוצה למקם מחדש',
    repositionNo: 'לא, הכל בסדר',
    askRepositionInstruction: 'מה למקם ואיפה? (למשל: "תעביר את הבקשה על הביטחונות לאחרי סעיף 22(ה)")',
    placeholderReposition: 'תאר/י מה להזיז ולאן…',
    repositioning: 'מזיז/ה את הבקשה למיקום החדש…',
    repositionDone: (moved) =>
      moved === 1 ? 'בוצע! הועברה בקשה אחת למיקום החדש.' : `בוצע! הועברו ${moved} בקשות למיקום החדש.`,
    repositionNone: 'לא הצלחתי לזהות בבירור מה להזיז ולאן — אפשר לנסח שוב, בצורה מפורטת יותר?',
    errorReposition: (msg) => `מצטער/ת, ההזזה נכשלה: ${msg}`,
    askRepositionAgain: 'משהו נוסף למקם מחדש?',
    askPendingHeading: (items, headings) =>
      `לא הייתי בטוח/ה תחת איזו כותרת לשתול ${items.length} בקש${items.length === 1 ? 'ה' : 'ות'}:\n` +
      items.map((t, i) => `${i + 1}. ${t}`).join('\n') +
      '\n\nתחת איזו כותרת לשתול אותן? לדוגמה: ' +
      headings.join(', ') +
      '\nמשם אחליט/ה לבד את הסדר המדויק בתוך הכותרת שתבחר/י.',
    placingPending: 'ממקם/ת את הבקשות תחת הכותרת שנבחרה…',
    pendingPlacedDone: (placed, headingLabel) =>
      placed === 1
        ? `בוצע! מוקמה בקשה אחת תחת "${headingLabel}".`
        : `בוצע! מוקמו ${placed} בקשות תחת "${headingLabel}".`,
    errorPlacePending: (msg) => `מצטער/ת, המיקום נכשל: ${msg}`,
    analyzingStrategy: 'בודק/ת איך למקם כל בקשה…',
    errorProposeStrategy: (msg) => `מצטער/ת, לא הצלחתי להציע אסטרטגיית מיקום: ${msg}`,
    wizardIntro: (totalGroups) =>
      totalGroups === 1
        ? 'מצאתי קבוצת אזכור אחת שדורשת החלטה שלך לגבי הכותרת המתאימה.'
        : `מצאתי ${totalGroups} קבוצות אזכור שדורשות החלטה שלך לגבי הכותרת המתאימה לכל אחת. אשאל אותך שאלה אחת לכל קבוצה:`,
    wizardProgress: (current, total) => `שאלה ${current}/${total}`,
    wizardQuestion: (count, signal, rationale) =>
      `מצאתי ${count} בקש${count === 1 ? 'ה המפנה' : 'ות המפנות'} ל"${signal}". ${rationale}\n\nתחת איזו כותרת לשתול אות${count === 1 ? 'ה' : 'ן'}?`,
    wizardQuestionNoSignal: (count, rationale) =>
      `מצאתי ${count} בקש${count === 1 ? 'ה כללית' : 'ות כלליות'} ללא הפניה מפורשת לנספח/הסכם. ${rationale}\n\nתחת איזו כותרת לשתול אות${count === 1 ? 'ה' : 'ן'}?`,
    wizardOptionOther: 'כותרת חדשה (הקלד/י)',
    askNewHeadingText: 'מה הכותרת החדשה שברצונך ליצור? היא תמוקם בסוף המסמך, אחרי תתי-הסעיפים של הכותרת האחרונה.',
    placeholderNewHeading: 'למשל: נספח ד\' - בקשות נוספות',
    wizardBackToOptions: '← חזרה לבחירת כותרת קיימת',
    errorEmptyNewHeading: 'יש להקליד שם לכותרת החדשה.',
    newHeadingConfirmed: (headingText) => `בסדר — אצור כותרת חדשה "${headingText}" בסוף המסמך.`,
    wizardAllAnswered: 'ענית על כל השאלות. בהמשך לכך:',
    wizardSummaryHeader: 'סיכום ההחלטות:',
    wizardDecisionLine: (count, signal, headingLabel) =>
      `- ${count} בקש${count === 1 ? 'ה' : 'ות'} ל"${signal}" ← ${headingLabel}`,
    generalGroupLabel: 'בקשות כלליות (ללא הפניה מפורשת)',
    wizardSubmit: 'אישור',
  },
  en: {
    welcomePaste:
      'Copy and paste the client requests from the email into the box below, then press Enter to send.',
    errorEmptyPaste: 'Please paste the client requests from the email.',
    foundRequests: (count) =>
      `I found ${count} client request${count !== 1 ? 's' : ''}. Where should I install them?`,
    errorAnalyze: (msg) => `Sorry, I could not analyze the pasted content: ${msg}`,
    askLocation: '',
    decideYourself: 'Decide for me',
    autoModeNotice:
      'Got it — I will choose the best location for each request. Requests with an explicit reference to a clause in the document body go near the relevant paragraph; general requests prefer a "General" / "General notes" section when available.',
    askChronological: 'Should I install the client requests in chronological order?',
    askOuterClauseMatchYes: 'Yes, match clause references in the document body',
    askOuterClauseMatchNo: 'No, install as a direct continuation from the last clause',
    appendAtLocationNotice: (locationLabel) =>
      `Got it — I'll install all requests at the bottom of "${locationLabel}" as a direct continuation from the last clause.`,
    askFormat: 'Should I install with track changes or as plain text?',
    chronologicalYes: 'Yes, chronological order',
    chronologicalNo: 'As a direct continuation from the last clause',
    formatTrackChanges: 'Track changes',
    formatPlain: 'Plain text',
    askAuthor: 'On behalf of who should the track changes be attributed?',
    authorMyName: (name) => `In my name: ${name}`,
    authorUnknown: 'Unknown',
    authorSomeoneElse: 'Someone else (I will specify the name)',
    askCustomAuthor:
      'What author name should I use for the track changes I insert into the document?',
    defaultAuthor: 'Unknown',
    installing: 'Installing client requests into your document…',
    doneSummary: (succeeded, failed) =>
      succeeded > 0
        ? `Done! Inserted ${succeeded} client request${succeeded !== 1 ? 's' : ''}${failed > 0 ? ` (${failed} failed)` : ''}.`
        : 'No client requests were inserted.',
    noneInserted: 'No client requests were inserted. Please check the document and try again.',
    errorExecute: (msg) => `Sorry, installation failed: ${msg}`,
    placeholderPaste: 'Paste client requests from email…',
    placeholderAuthor: 'e.g. Client Name (leave empty for Unknown)',
    selfChecking: 'Checking that everything was placed correctly…',
    selfCheckFoundIssues: (labels) =>
      `I ran a check and found that the following were not placed correctly: ${labels.join(', ')}. Fixing...`,
    selfCheckFixed: (count) =>
      count === 1 ? 'Fixed! 1 clause was moved to the correct position.' : `Fixed! ${count} clauses were moved to the correct position.`,
    selfCheckAllCorrect: 'I checked the placement — everything looks correct.',
    askReposition:
      'Are there any requests that were not installed correctly that you would like me to reposition? If so, tell me what and where to place them.',
    repositionYes: 'Yes, I want to reposition',
    repositionNo: "No, it's fine",
    askRepositionInstruction: 'What should move, and where? (e.g. "move the deposits request to after clause 22(e)")',
    placeholderReposition: 'Describe what to move and where…',
    repositioning: 'Moving the request to its new location…',
    repositionDone: (moved) =>
      moved === 1 ? 'Done! Moved 1 request to its new location.' : `Done! Moved ${moved} requests to their new location.`,
    repositionNone: "I couldn't clearly tell what to move and where — could you describe it in more detail?",
    errorReposition: (msg) => `Sorry, the move failed: ${msg}`,
    askRepositionAgain: 'Anything else to reposition?',
    askPendingHeading: (items, headings) =>
      `I wasn't confident which heading ${items.length} request${items.length === 1 ? '' : 's'} belong${items.length === 1 ? 's' : ''} under:\n` +
      items.map((t, i) => `${i + 1}. ${t}`).join('\n') +
      '\n\nWhich heading should I place them under? For example: ' +
      headings.join(', ') +
      "\nFrom there I'll decide the exact order within that heading on my own.",
    placingPending: 'Placing the requests under the chosen heading…',
    pendingPlacedDone: (placed, headingLabel) =>
      placed === 1
        ? `Done! Placed 1 request under "${headingLabel}".`
        : `Done! Placed ${placed} requests under "${headingLabel}".`,
    errorPlacePending: (msg) => `Sorry, placement failed: ${msg}`,
    analyzingStrategy: 'Working out how to place each request…',
    errorProposeStrategy: (msg) => `Sorry, I could not propose a placement strategy: ${msg}`,
    wizardIntro: (totalGroups) =>
      totalGroups === 1
        ? 'I found one citation group that needs your decision on the right heading.'
        : `I found ${totalGroups} citation groups that need your decision on the right heading. I'll ask one question per group:`,
    wizardProgress: (current, total) => `Question ${current}/${total}`,
    wizardQuestion: (count, signal, rationale) =>
      `I found ${count} request${count !== 1 ? 's' : ''} referencing "${signal}". ${rationale}\n\nWhich heading should ${count === 1 ? 'it' : 'they'} be assigned to?`,
    wizardQuestionNoSignal: (count, rationale) =>
      `I found ${count} general request${count !== 1 ? 's' : ''} with no explicit reference to an appendix/agreement. ${rationale}\n\nWhich heading should ${count === 1 ? 'it' : 'they'} be assigned to?`,
    wizardOptionOther: 'New heading (type it in)',
    askNewHeadingText: 'What should the new heading be called? It will be placed at the end of the document, after the last heading\'s sub-clauses.',
    placeholderNewHeading: 'e.g. Appendix D - Additional Requests',
    wizardBackToOptions: '← Back to choosing an existing heading',
    errorEmptyNewHeading: 'Please type a name for the new heading.',
    newHeadingConfirmed: (headingText) => `Got it — I'll create a new heading "${headingText}" at the end of the document.`,
    wizardAllAnswered: "You've answered every question. Next:",
    wizardSummaryHeader: 'Decisions:',
    wizardDecisionLine: (count, signal, headingLabel) =>
      `- ${count} request${count !== 1 ? 's' : ''} referencing "${signal}" ← ${headingLabel}`,
    generalGroupLabel: 'General requests (no explicit reference)',
    wizardSubmit: 'Confirm',
  },
};

export function getSkillRunnerCopy(language: SupportedLanguage = 'he'): SkillRunnerCopy {
  return COPY[language] ?? COPY.he;
}
